import { useCallback, useEffect, useMemo, useState } from 'react';
import { Layout } from '@/components/Layout';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatDate } from '@/lib/format';
import { useSEO } from '@/lib/useSEO';
import { toast } from 'sonner';
import { Search, Eye, Send } from 'lucide-react';
import { Pagination, usePagination } from '@/components/Pagination';

const STATUS = {
  radicada: { label: 'Radicada', cls: 'bg-slate-100 text-slate-800 border-slate-200' },
  en_revision: { label: 'En revisión', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  en_gestion: { label: 'En gestión', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  respondida: { label: 'Respondida', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  cerrada: { label: 'Cerrada', cls: 'bg-slate-900 text-white border-slate-900' },
  // Legacy compatibility (documents created before iteración 28)
  open: { label: 'Radicada', cls: 'bg-slate-100 text-slate-800 border-slate-200' },
  in_progress: { label: 'En gestión', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  resolved: { label: 'Cerrada', cls: 'bg-slate-900 text-white border-slate-900' },
};
const STATE_ORDER = ['radicada', 'en_revision', 'en_gestion', 'respondida', 'cerrada'];
const TYPE_LABEL = {
  petition: 'Petición', complaint: 'Queja', claim: 'Reclamo', suggestion: 'Sugerencia',
};

export default function AdminPQR() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('all');
  const [type, setType] = useState('all');
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState('');

  useSEO({
    title: 'PQR | Administración',
    description: 'Gestiona peticiones, quejas, reclamos y sugerencias.',
    canonical: '/admin/pqr',
  });

  const refresh = useCallback(() => api.get('/admin/pqr', { params: status !== 'all' ? { status } : {} })
    .then(({ data }) => setItems(data.items || [])), [status]);
  useEffect(() => { refresh(); }, [refresh]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return items.filter((it) => {
      if (type !== 'all' && it.type !== type) return false;
      if (!s) return true;
      return (
        (it.subject || '').toLowerCase().includes(s) ||
        (it.pqr_id || '').toLowerCase().includes(s) ||
        (it.description || '').toLowerCase().includes(s)
      );
    });
  }, [items, q, type]);
  const { pageItems, page, setPage, totalPages, total } = usePagination(filtered);

  const openDetail = async (pqr_id) => {
    const { data } = await api.get(`/admin/pqr/${pqr_id}`);
    setDetail(data);
    setReply('');
  };
  const closeDetail = () => setDetail(null);

  const updateStatus = async (newStatus) => {
    try {
      await api.patch(`/admin/pqr/${detail.pqr.pqr_id}`, { status: newStatus });
      toast.success('Estado actualizado');
      const { data } = await api.get(`/admin/pqr/${detail.pqr.pqr_id}`);
      setDetail(data);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };
  const sendReply = async () => {
    const text = reply.trim();
    if (!text) return;
    try {
      await api.post(`/admin/pqr/${detail.pqr.pqr_id}/reply`, { text });
      toast.success('Respuesta enviada');
      setReply('');
      const { data } = await api.get(`/admin/pqr/${detail.pqr.pqr_id}`);
      setDetail(data);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-orange-600">Administración</div>
            <h1 className="font-display text-3xl font-black tracking-tighter text-slate-900 mt-1">PQR</h1>
          </div>
          <div className="flex gap-2 flex-wrap">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2 top-2.5 text-slate-400" />
              <Input
                data-testid="admin-pqr-search"
                placeholder="Buscar por radicado, asunto o texto"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="rounded-none pl-8 w-72"
              />
            </div>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="admin-pqr-type-filter" className="w-40 rounded-none"><SelectValue placeholder="Tipo" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                <SelectItem value="petition">Peticiones</SelectItem>
                <SelectItem value="complaint">Quejas</SelectItem>
                <SelectItem value="claim">Reclamos</SelectItem>
                <SelectItem value="suggestion">Sugerencias</SelectItem>
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="admin-pqr-status-filter" className="w-40 rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="radicada">Radicadas</SelectItem>
                <SelectItem value="en_revision">En revisión</SelectItem>
                <SelectItem value="en_gestion">En gestión</SelectItem>
                <SelectItem value="respondida">Respondidas</SelectItem>
                <SelectItem value="cerrada">Cerradas</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-6 border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3">Radicado</th>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Asunto</th>
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {pageItems.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500">Sin resultados</td></tr>}
              {pageItems.map((it) => {
                const st = STATUS[it.status] || { label: it.status, cls: 'bg-slate-100' };
                return (
                  <tr key={it.pqr_id} data-testid={`admin-pqr-row-${it.pqr_id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{it.ticket_number || it.pqr_id}</td>
                    <td className="px-4 py-3">{TYPE_LABEL[it.type] || it.type}</td>
                    <td className="px-4 py-3">{it.subject}</td>
                    <td className="px-4 py-3 text-slate-500">{formatDate(it.created_at)}</td>
                    <td className="px-4 py-3"><Badge className={`rounded-none border ${st.cls}`}>{st.label}</Badge></td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        data-testid={`admin-pqr-detail-${it.pqr_id}`}
                        variant="outline" size="sm" className="rounded-none"
                        onClick={() => openDetail(it.pqr_id)}
                      >
                        <Eye className="mr-1 h-3.5 w-3.5" /> Ver
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Pagination page={page} totalPages={totalPages} total={total} setPage={setPage} testIdPrefix="admin-pqr-pag" />

        <Dialog open={!!detail} onOpenChange={(o) => !o && closeDetail()}>
          <DialogContent className="rounded-none max-w-2xl bg-white max-h-[90vh] overflow-y-auto" data-testid="admin-pqr-detail-dialog">
            {detail && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center justify-between gap-3">
                    <span className="font-mono text-sm" data-testid="admin-pqr-detail-ref">
                      {detail.pqr.ticket_number || detail.pqr.pqr_id}
                    </span>
                    <Badge className={`rounded-none border ${(STATUS[detail.pqr.status] || {}).cls}`}>
                      {(STATUS[detail.pqr.status] || {}).label || detail.pqr.status}
                    </Badge>
                  </DialogTitle>
                </DialogHeader>
                <div className="text-sm">
                  <div className="text-slate-500 text-xs uppercase">Asunto</div>
                  <div className="font-semibold">{detail.pqr.subject}</div>
                  <div className="mt-3 text-slate-500 text-xs uppercase">Descripción</div>
                  <div className="whitespace-pre-wrap">{detail.pqr.description}</div>
                  {detail.user && (
                    <div className="mt-3 border-t border-slate-200 pt-3 text-xs text-slate-600">
                      Usuario: <strong>{detail.user.name}</strong> ({detail.user.email}) · Registro {formatDate(detail.user.created_at)}
                    </div>
                  )}
                </div>

                {/* Historial de respuestas */}
                {(detail.pqr.replies || []).length > 0 && (
                  <div className="mt-4 border-t border-slate-200 pt-3">
                    <div className="text-xs uppercase text-slate-500 mb-2">Respuestas</div>
                    <div className="space-y-2">
                      {detail.pqr.replies.map((r, i) => (
                        <div key={i} className="border border-slate-200 p-3 bg-slate-50 text-sm">
                          <div className="text-xs text-slate-500">
                            <strong>{r.author_name}</strong> · {formatDate(r.created_at)}
                          </div>
                          <div className="whitespace-pre-wrap mt-1">{r.text}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-4 border-t border-slate-200 pt-3 space-y-3">
                  <Textarea
                    data-testid="admin-pqr-reply-input"
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    rows={3}
                    placeholder="Escribe tu respuesta al usuario…"
                    className="rounded-none"
                  />
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      data-testid="admin-pqr-send-reply"
                      onClick={sendReply}
                      disabled={!reply.trim()}
                      className="rounded-none bg-slate-900 hover:bg-slate-800 text-white"
                    >
                      <Send className="mr-2 h-4 w-4" /> Enviar respuesta
                    </Button>
                    <Button
                      data-testid="admin-pqr-mark-en-revision"
                      variant="outline" className="rounded-none border-amber-300 text-amber-700"
                      onClick={() => updateStatus('en_revision')}
                    >
                      En revisión
                    </Button>
                    <Button
                      data-testid="admin-pqr-mark-en-gestion"
                      variant="outline" className="rounded-none border-blue-300 text-blue-700"
                      onClick={() => updateStatus('en_gestion')}
                    >
                      En gestión
                    </Button>
                    <Button
                      data-testid="admin-pqr-mark-respondida"
                      variant="outline" className="rounded-none border-emerald-300 text-emerald-700"
                      onClick={() => updateStatus('respondida')}
                    >
                      Respondida
                    </Button>
                    <Button
                      data-testid="admin-pqr-mark-cerrada"
                      variant="outline" className="rounded-none border-slate-800 text-slate-900"
                      onClick={() => updateStatus('cerrada')}
                    >
                      Cerrar
                    </Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
